import React, { useState } from 'react';
import { Code2, Copy, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useApp } from '../context/AppContext';
import { Pill } from './common';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { cn } from '../lib/utils';

const base = process.env.REACT_APP_BACKEND_URL;

const snippets = {
  javascript: (url, key, pk) => `const params = new URLSearchParams(${JSON.stringify(pk, null, 2)});
// Browser code? Add your site to this client's "Allowed browser origins" first, and keep credentials: 'omit'.
const res = await fetch(\`${url}?\${params}\`, {
  headers: { 'X-API-Key': '${key || 'kyc_live_…'}', 'X-Correlation-Id': crypto.randomUUID() },
  credentials: 'omit',
});
const body = await res.json();
if (body.status === 'COMPLIANT') { /* proceed */ }
else if (body.requestUrl) { /* send the customer to body.requestUrl */ }
console.log(body.status, body.pendingItems);`,
  python: (url, key, pk) => `import requests, uuid

params = ${JSON.stringify(pk, null, 4).replace(/"/g, "'")}
res = requests.get(
    "${url}",
    params=params,
    headers={"X-API-Key": "${key || 'kyc_live_…'}", "X-Correlation-Id": str(uuid.uuid4())},
    timeout=10,
)
body = res.json()
if body["status"] == "COMPLIANT":
    ...  # proceed
elif body.get("requestUrl"):
    ...  # send the customer to body["requestUrl"]
print(body["status"], body["pendingItems"])`,
  curl: (url, key, pk) => `curl -s "${url}?${new URLSearchParams(pk).toString()}" \\
  -H "X-API-Key: ${key || 'kyc_live_…'}"`,
};

export const CodeGenerator = () => {
  const app = useApp();
  const classes = app.classes.filter((c) => c.status === 'active');
  const [lang, setLang] = useState('javascript');
  const [classId, setClassId] = useState(classes[0]?.id || '');
  const [key, setKey] = useState('');
  const [pk, setPk] = useState({});
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const cls = classes.find((c) => c.id === classId);
  const pkFields = (cls?.primaryKeyFieldIds || []).map((fid) => app.fields.find((f) => f.id === fid)).filter(Boolean);
  const url = `${base}/api/v1/kyc/status`;
  const params = { classCode: cls?.code || 'IND', ...Object.fromEntries(pkFields.map((f) => [f.code, pk[f.code] || (f.example ? f.example : `<${f.code}>`)])) };
  const code = snippets[lang](url, key, params);

  const run = async () => {
    if (!key) { toast.error('Paste an API key first (created on this page; shown once).'); return; }
    setBusy(true);
    const started = performance.now();
    try {
      const r = await fetch(`${url}?${new URLSearchParams(params)}`, { headers: { 'X-API-Key': key } });
      const body = await r.json().catch(() => ({}));
      setRes({ status: r.status, ms: Math.round(performance.now() - started), body });
    } catch (e) { setRes({ status: 0, ms: Math.round(performance.now() - started), body: { error: String(e) } }); }
    setBusy(false);
  };

  return (
    <div className="rounded-xl border border-line bg-white shadow-card overflow-hidden" data-testid="code-generator">
      <div className="px-5 py-4 border-b border-line flex flex-wrap items-center gap-3"><div className="flex items-center gap-2 font-semibold text-ink-800"><Code2 className="h-4 w-4 text-ink-500" />Call the status API</div><span className="text-xs text-ink-400">Fill in your key and primary key, copy the snippet or run it live from here.</span></div>
      <div className="grid lg:grid-cols-[360px_1fr] divide-y lg:divide-y-0 lg:divide-x divide-line">
        <div className="p-5 space-y-3">
          <div className="space-y-1.5"><Label className="text-xs">Class</Label><Select value={classId} onValueChange={(v) => { setClassId(v); setPk({}); }}><SelectTrigger data-testid="codegen-class" className="h-9"><SelectValue placeholder="Class…" /></SelectTrigger><SelectContent>{classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name} ({c.code})</SelectItem>)}</SelectContent></Select></div>
          <div className="space-y-1.5"><Label className="text-xs">API key (X-API-Key)</Label><Input data-testid="codegen-key" type="password" value={key} onChange={(e) => setKey(e.target.value)} placeholder="kyc_test_…" className="h-9 font-mono text-xs" /></div>
          {pkFields.map((f) => <div key={f.id} className="space-y-1.5"><Label className="text-xs">{f.name} <span className="font-mono text-ink-400">{f.code}</span></Label><Input data-testid={`codegen-pk-${f.code}`} value={pk[f.code] || ''} onChange={(e) => setPk({ ...pk, [f.code]: e.target.value })} placeholder={f.example || f.name} className="h-9 font-mono text-xs" /></div>)}
          <div className="flex items-center gap-2 pt-1"><Button data-testid="codegen-run" onClick={run} disabled={busy} className="bg-ink-700 hover:bg-ink-800 text-white"><Play className="h-3.5 w-3.5 mr-1.5" />{busy ? 'Calling…' : 'Live fetch'}</Button><span className="text-[11px] text-ink-400">Runs from your browser against {base.replace(/^https?:\/\//, '')}</span></div>
        </div>
        <div className="p-5 space-y-3 min-w-0">
          <div className="flex items-center gap-1.5">{Object.keys(snippets).map((l) => <button key={l} type="button" data-testid={`codegen-lang-${l}`} onClick={() => setLang(l)} className={cn('px-2.5 py-1 rounded-md text-xs font-medium border transition-colors', lang === l ? 'bg-ink-700 text-white border-ink-700' : 'border-line text-ink-600 hover:bg-paper-50')}>{l === 'javascript' ? 'JavaScript' : l === 'python' ? 'Python' : 'cURL'}</button>)}<Button size="sm" variant="outline" className="ml-auto h-7 text-xs" onClick={() => { navigator.clipboard?.writeText(code); toast.success('Snippet copied'); }}><Copy className="h-3 w-3 mr-1" />Copy</Button></div>
          <pre data-testid="codegen-snippet" className="rounded-lg bg-ink-800 text-paper-50 font-mono text-[11.5px] p-4 leading-relaxed overflow-auto max-h-[300px] whitespace-pre">{code}</pre>
          {res && <div data-testid="codegen-result" className="rounded-lg border border-line bg-paper-50 p-3 space-y-2"><div className="flex items-center gap-2 text-xs"><Pill tone={res.status >= 200 && res.status < 300 ? 'emerald' : 'rose'}>HTTP {res.status || 'network error'}</Pill><Pill>{res.ms} ms</Pill>{res.body?.status && <Pill tone="sky">{res.body.status}</Pill>}{res.body?.requestUrl && <a href={res.body.requestUrl} target="_blank" rel="noreferrer" className="text-xs text-ink-700 underline truncate max-w-[280px]">{res.body.requestUrl}</a>}</div><pre className="max-h-56 overflow-auto font-mono text-[11px] text-ink-700">{JSON.stringify(res.body, null, 2)}</pre></div>}
        </div>
      </div>
    </div>
  );
};
